import type { LeadStatus, LeadSubStatus } from '@/api/leads';

/** Tab / filter order for lead pipeline */
export const LEAD_STATUS_OPTIONS: LeadStatus[] = [
  'NEW',
  'CONTACTED',
  'INTERESTED',
  'FOLLOW_UP',
  'CONVERTED',
  'LOST',
  'CLOSED',
];

const statusBadgeBase =
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border';

export function leadStatusBadgeClass(status: string, options?: { frtOverdue?: boolean }): string {
  if (
    options?.frtOverdue &&
    status !== 'LOST' &&
    status !== 'CLOSED'
  ) {
    return `${statusBadgeBase} bg-red-100 text-red-900 border-red-400 ring-1 ring-red-300`;
  }
  switch (status) {
    case 'NEW':
      return `${statusBadgeBase} bg-sky-100 text-sky-900 border-sky-200`;
    case 'CONTACTED':
      return `${statusBadgeBase} bg-amber-100 text-amber-900 border-amber-200`;
    case 'INTERESTED':
      return `${statusBadgeBase} bg-teal-100 text-teal-900 border-teal-200`;
    case 'FOLLOW_UP':
      return `${statusBadgeBase} bg-violet-100 text-violet-900 border-violet-200`;
    case 'CONVERTED':
      return `${statusBadgeBase} bg-emerald-100 text-emerald-900 border-emerald-200`;
    case 'LOST':
      return `${statusBadgeBase} bg-slate-200 text-slate-800 border-slate-300`;
    case 'CLOSED':
      return `${statusBadgeBase} bg-lime-100 text-lime-900 border-lime-200`;
    default:
      return `${statusBadgeBase} bg-gray-100 text-gray-700 border-gray-200`;
  }
}

const priorityBadgeBase =
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border';

/** Score bands: HIGH ≥70 (green), MEDIUM 40–69 (yellow), LOW &lt;40 (red). Label used when score is absent. */
export function leadPriorityBadgeClass(label?: string | null, score?: number | null): string {
  const s = score != null && !Number.isNaN(Number(score)) ? Number(score) : null;
  const l = (label || 'MEDIUM').toUpperCase();

  if (s != null) {
    if (s >= 70) {
      if (s >= 90) return `${priorityBadgeBase} bg-emerald-700 text-white border-emerald-800`;
      if (s >= 80) return `${priorityBadgeBase} bg-emerald-500 text-white border-emerald-600`;
      return `${priorityBadgeBase} bg-emerald-100 text-emerald-950 border-emerald-400`;
    }
    if (s >= 40) {
      if (s >= 60) return `${priorityBadgeBase} bg-amber-200 text-amber-950 border-amber-400`;
      if (s >= 50) return `${priorityBadgeBase} bg-amber-100 text-amber-900 border-amber-300`;
      return `${priorityBadgeBase} bg-yellow-100 text-yellow-950 border-yellow-300`;
    }
    if (s < 20) return `${priorityBadgeBase} bg-red-200 text-red-950 border-red-400`;
    if (s < 30) return `${priorityBadgeBase} bg-red-100 text-red-900 border-red-300`;
    return `${priorityBadgeBase} bg-rose-100 text-rose-900 border-rose-300`;
  }

  if (l === 'HIGH') return `${priorityBadgeBase} bg-emerald-100 text-emerald-950 border-emerald-400`;
  if (l === 'LOW') return `${priorityBadgeBase} bg-red-100 text-red-900 border-red-300`;
  return `${priorityBadgeBase} bg-amber-100 text-amber-900 border-amber-300`;
}

export function formatLeadStatusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  return status.replace(/_/g, ' ');
}

export const LEAD_SUBSTATUS_OPTIONS_BY_STATUS: Record<LeadStatus, LeadSubStatus[]> = {
  NEW: ['NOT_CONTACTED', 'AUTO_GENERATED', 'WALK_IN_ENQUIRY', 'REFERRED_LEAD'],
  CONTACTED: [
    'CONNECTED_DETAILS_SHARED',
    'CONNECTED_NEEDS_FOLLOW_UP',
    'NOT_REACHABLE',
    'SWITCHED_OFF',
  ],
  INTERESTED: [
    'ASKED_FOR_COST',
    'ASKED_FOR_DOCTOR_INFO',
    'ASKED_FOR_APPOINTMENT',
    'WANTS_CALL_BACK',
    'COMPARING_OPTIONS',
  ],
  FOLLOW_UP: [
    'FOLLOW_UP_SCHEDULED',
    'AWAITING_PATIENT_DECISION',
    'AWAITING_REPORTS',
    'AWAITING_FAMILY_DISCUSSION',
    'RESCHEDULED_CALL',
    'NO_RESPONSE_AFTER_FOLLOW_UP',
  ],
  CONVERTED: [
    'OPD_BOOKED',
    'OPD_VISITED',
    'IPD_ADMITTED',
    'PROCEDURE_BOOKED',
    'DAY_CARE_DONE',
  ],
  LOST: [
    'PRICE_TOO_HIGH',
    'WENT_TO_COMPETITOR',
    'NOT_INTERESTED',
    'TREATMENT_POSTPONED',
    'LOCATION_ISSUE',
    'TRUST_ISSUE',
    'NO_RESPONSE',
    'DUPLICATE_LEAD',
    'WRONG_NUMBER',
    'LONG_TERM_FOLLOW_UP',
  ],
  CLOSED: ['SUCCESSFULLY_TREATED', 'CASE_COMPLETED', 'LONG_TERM_FOLLOW_UP'],
};

const LEAD_SUBSTATUS_LABELS: Record<LeadSubStatus, string> = {
  NOT_CONTACTED: 'Not Contacted',
  AUTO_GENERATED: 'Auto Generated (Website / Ads)',
  WALK_IN_ENQUIRY: 'Walk-in Enquiry',
  REFERRED_LEAD: 'Referred Lead',

  CONNECTED_DETAILS_SHARED: 'Connected - Details Shared',
  CONNECTED_NEEDS_FOLLOW_UP: 'Connected - Needs Follow-up',
  NOT_REACHABLE: 'Not Reachable',
  SWITCHED_OFF: 'Switched Off',

  ASKED_FOR_COST: 'Asked for Cost',
  ASKED_FOR_DOCTOR_INFO: 'Asked for Doctor Info',
  ASKED_FOR_APPOINTMENT: 'Asked for Appointment',
  WANTS_CALL_BACK: 'Wants Call Back',
  COMPARING_OPTIONS: 'Comparing Options',

  FOLLOW_UP_SCHEDULED: 'Follow-up Scheduled',
  AWAITING_PATIENT_DECISION: 'Awaiting Patient Decision',
  AWAITING_REPORTS: 'Awaiting Reports',
  AWAITING_FAMILY_DISCUSSION: 'Awaiting Family Discussion',
  RESCHEDULED_CALL: 'Rescheduled Call',
  NO_RESPONSE_AFTER_FOLLOW_UP: 'No Response After Follow-up',

  OPD_BOOKED: 'OPD Booked',
  OPD_VISITED: 'OPD Visited',
  IPD_ADMITTED: 'IPD Admitted',
  PROCEDURE_BOOKED: 'Procedure Booked',
  DAY_CARE_DONE: 'Day Care Done',

  PRICE_TOO_HIGH: 'Price Too High',
  WENT_TO_COMPETITOR: 'Went to Competitor',
  NOT_INTERESTED: 'Not Interested',
  TREATMENT_POSTPONED: 'Treatment Postponed',
  LOCATION_ISSUE: 'Location Issue',
  TRUST_ISSUE: 'Trust Issue',
  NO_RESPONSE: 'No Response',
  DUPLICATE_LEAD: 'Duplicate Lead',
  WRONG_NUMBER: 'Wrong Number',

  SUCCESSFULLY_TREATED: 'Successfully Treated',
  CASE_COMPLETED: 'Case Completed',
  LONG_TERM_FOLLOW_UP: 'Long-term Follow-up',
};

export function formatLeadSubStatusLabel(subStatus?: LeadSubStatus | null): string {
  if (!subStatus) return 'Not specified';
  return LEAD_SUBSTATUS_LABELS[subStatus] ?? subStatus.replace(/_/g, ' ');
}

/** Sub-status line for leads list; appends DNP x/9 when currently Not Reachable. */
export function formatLeadSubStatusLineWithDnp(
  subStatus?: LeadSubStatus | null,
  notReachableAttemptTotal?: number | null
): string {
  if (!subStatus) return '—';
  const label = formatLeadSubStatusLabel(subStatus);
  if (subStatus !== 'NOT_REACHABLE') return label;
  const n = Math.max(0, Number(notReachableAttemptTotal ?? 0));
  return `${label} · DNP ${n}/9`;
}

/** Second line on leads list when a repeat enquiry was reset to NEW. */
export function formatPriorLeadStatusLine(
  priorStatus?: LeadStatus | null,
  priorSubStatus?: LeadSubStatus | null
): string {
  if (!priorStatus) return '';
  const main = formatLeadStatusLabel(priorStatus);
  if (!priorSubStatus) return main;
  return `${main} · ${formatLeadSubStatusLabel(priorSubStatus)}`;
}
